import { appKey } from '@app/shared/brand'
import { useSyncExternalStore } from 'react'
import { migrateDocStorage } from './doc-storage-migrations'

/**
 * The documents a person has open, like an editor's tabs.
 *
 * Kept per browser rather than per account: which documents are open is where
 * somebody left off on this machine, and another device has its own place.
 * Newest last, and a document already open keeps its place, so the list does
 * not reorder under the pointer as a person moves through it.
 */
export type OpenDoc = {
  kind: 'book' | KeyedDocKind
  /** A book's id, or a page's, deck's or board's key — what its address names. */
  ref: string
  /** The space a shared document is read from, as its address carries it. */
  spaceId?: string
}

/**
 * Where each document addressed by its key opens. A board answers at
 * `/canvases`, because `/board` is a shul's screens.
 */
export const KEYED_DOC_PATH = {
  page: '/pages/$key',
  deck: '/decks/$key',
  board: '/canvases/$key',
} as const

/** A document opened by its key within a space, rather than by its id. */
export type KeyedDocKind = keyof typeof KEYED_DOC_PATH

const KEY = appKey('openDocs')
/** Enough to hold a working session; more is a list nobody reads. */
const MAX_OPEN = 12

const KINDS = new Set(['book', ...Object.keys(KEYED_DOC_PATH)])

function isOpenDoc(v: unknown): v is OpenDoc {
  if (typeof v !== 'object' || v === null) return false
  const w = v as Record<string, unknown>
  return (
    typeof w.kind === 'string' &&
    KINDS.has(w.kind) &&
    typeof w.ref === 'string' &&
    (w.spaceId === undefined || typeof w.spaceId === 'string')
  )
}

/* Storage can be blocked or hold something an older build wrote, and either
   leaves the list empty rather than the app broken. */
function read(): OpenDoc[] {
  try {
    migrateDocStorage()
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter(isOpenDoc) : []
  } catch {
    return []
  }
}

let current: OpenDoc[] | null = null
const listeners = new Set<() => void>()

function snapshot(): OpenDoc[] {
  if (!current) current = read()
  return current
}

function write(next: OpenDoc[]) {
  current = next
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* Held for this visit; the next one starts empty. */
  }
  for (const listen of listeners) listen()
}

function subscribe(listen: () => void) {
  listeners.add(listen)
  return () => {
    listeners.delete(listen)
  }
}

export const sameDoc = (a: OpenDoc, b: OpenDoc) =>
  a.kind === b.kind && a.ref === b.ref

export function openDoc(studioDoc: OpenDoc) {
  const list = snapshot()
  if (list.some((w) => sameDoc(w, studioDoc))) return
  write([...list, studioDoc].slice(-MAX_OPEN))
}

export function closeDoc(studioDoc: OpenDoc) {
  write(snapshot().filter((w) => !sameDoc(w, studioDoc)))
}

/** Signing out leaves none of the person's documents behind on the machine. */
export function clearOpenDocs() {
  write([])
}

export function useOpenDocs(): OpenDoc[] {
  return useSyncExternalStore(subscribe, snapshot)
}

const AT = /^\/(books|pages|decks|canvases)\/([^/]+)/
const KIND_AT = {
  books: 'book',
  pages: 'page',
  decks: 'deck',
  canvases: 'board',
} as const

/** The document an address opens, or null where it opens none. */
export function docAt(pathname: string, search: string): OpenDoc | null {
  const hit = pathname.match(AT)
  if (!hit) return null
  const [, section, ref] = hit
  if (section === 'pages' && ref === 'new') return null
  const spaceId = new URLSearchParams(search).get('spaceId') ?? undefined
  return {
    kind: KIND_AT[section as keyof typeof KIND_AT],
    ref: decodeURIComponent(ref),
    ...(spaceId ? { spaceId } : {}),
  }
}
